import { useEffect, useRef } from 'react';

/**
 * A custom hook that manages the document title.
 *
 * @param title - The title to set for the document. If null or undefined,
 * the original title will be maintained
 *
 * @example
 * function MyComponent() {
 *   useDocumentTitle('My Page Title');
 *   return <div>My Component</div>;
 * }
 *
 * @throws {Error} If there's an issue setting the document title
 * @returns {void}
 *
 * @remarks
 * - The hook preserves the original document title and restores it on unmount
 * - If the title parameter is null or undefined, it maintains the original title
 * - Error handling is implemented to prevent crashes if title setting fails
 */
export const useDocumentTitle = (title?: string | null) => {
  const originalTitle = useRef(document.title);

  useEffect(() => {
    try {
      document.title = title ? `${title}` : originalTitle.current;
    } catch (error) {
      console.error('Error setting document title:', error);
      document.title = originalTitle.current;
    }

    return () => {
      document.title = originalTitle.current;
    };
  }, [title]);
};
